<template>
  <div class="pt-4">
    <h5 class="text-h5 font-weight-bold mb-4">Reminders</h5>
    <v-row>
      <v-col cols="12" md="3">
        <v-card outlined raised>
          <v-card-title class="subtitle-1 font-weight-bold">Filters</v-card-title>
          <v-card-text>
            <v-select
                v-model="selectedModule"
                :items="moduleOptions"
                item-text="label"
                item-value="value"
                label="Module"
                outlined
                dense
                hide-details
                class="mb-4"
            />

            <v-text-field
                v-model="filterStartDate"
                type="date"
                label="From date"
                outlined
                dense
                hide-details
                class="mb-4"
            />

            <v-text-field
                v-model="filterEndDate"
                type="date"
                label="To date"
                outlined
                dense
                hide-details
                class="mb-4"
            />

            <v-btn color="primary" block :loading="loading" @click="loadReminders(true)">
              Apply filters
            </v-btn>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" md="9">
        <v-card outlined raised>
          <v-card-title class="d-flex align-center">
            <v-btn icon small @click="previousMonth">
              <v-icon>mdi-chevron-left</v-icon>
            </v-btn>
            <span class="mx-4 text-h6">{{ monthLabel }}</span>
            <v-btn icon small @click="nextMonth">
              <v-icon>mdi-chevron-right</v-icon>
            </v-btn>
            <v-spacer></v-spacer>
            <v-btn text small color="primary" @click="goToToday">Today</v-btn>
          </v-card-title>

          <v-card-text>
            <div class="calendar-grid">
              <div
                  v-for="dayName in weekDays"
                  :key="dayName"
                  class="calendar-weekday text-center font-weight-bold"
              >
                {{ dayName }}
              </div>

              <div
                  v-for="(cell, index) in calendarCells"
                  :key="index"
                  class="calendar-cell"
                  :class="{
                    'calendar-cell--muted': !cell.inMonth,
                    'calendar-cell--today': cell.isToday
                  }"
              >
                <div class="calendar-cell__day">{{ cell.day }}</div>
                <div class="calendar-cell__events">
                  <div
                      v-for="reminder in cell.visibleReminders"
                      :key="reminder.id"
                      class="calendar-event"
                      @click="openRemindersModal(cell)"
                  >
                    <span class="calendar-event__time">{{ formatReminderTime(reminder.start_date) }}</span>
                    <span class="calendar-event__title">{{ reminderTitle(reminder) }}</span>
                  </div>
                  <div
                      v-if="cell.extraCount > 0"
                      class="calendar-event calendar-event--more"
                      @click="openRemindersModal(cell)"
                  >
                    +{{ cell.extraCount }}
                  </div>
                </div>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-dialog v-model="remindersDialog" max-width="900px" scrollable>
      <v-card>
        <v-card-title class="d-flex align-center">
          Reminders — {{ selectedDateLabel }}
          <v-spacer></v-spacer>
          <v-btn icon @click="remindersDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-data-table
              :headers="reminderTableHeaders"
              :items="selectedDateReminders"
              :items-per-page="-1"
              class="elevation-0"
              hide-default-footer
          >
            <template v-slot:[`item.contact_name`]="{ item }">
              {{ reminderTitle(item) }}
            </template>
            <template v-slot:[`item.start_date`]="{ item }">
              {{ formatDateAndTime(item.start_date) }}
            </template>
            <template v-slot:[`item.text`]="{ item }">
              <span v-html="replaceNewLine(item.text)"></span>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import moment from "moment";
import {CALENDAR_REMINDERS} from "@/core/services/store/calendar.module";

const MAX_VISIBLE_REMINDERS = 3;

export default {
  name: "Calendar",
  data() {
    const today = moment();
    return {
      loading: false,
      reminders: [],
      remindersDialog: false,
      selectedDateKey: "",
      selectedDateReminders: [],
      reminderTableHeaders: [
        {text: "Contact", value: "contact_name", sortable: false},
        {text: "Date & Time", value: "start_date", sortable: false},
        {text: "Comment", value: "text", sortable: false},
      ],
      selectedModule: "contacts",
      moduleOptions: [
        {label: "Contacts", value: "contacts"},
      ],
      filterStartDate: today.clone().startOf("month").format("YYYY-MM-DD"),
      filterEndDate: today.clone().endOf("month").format("YYYY-MM-DD"),
      currentMonth: today.clone().startOf("month"),
      weekDays: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
    };
  },
  computed: {
    monthLabel() {
      return this.currentMonth.format("MMMM YYYY");
    },
    selectedDateLabel() {
      if (!this.selectedDateKey) {
        return "";
      }
      return moment(this.selectedDateKey).format("dddd, MMMM D, YYYY");
    },
    remindersByDate() {
      const grouped = {};
      this.reminders.forEach(reminder => {
        if (!reminder.start_date) {
          return;
        }
        const key = moment(reminder.start_date).format("YYYY-MM-DD");
        if (!grouped[key]) {
          grouped[key] = [];
        }
        grouped[key].push(reminder);
      });

      Object.keys(grouped).forEach((key) => {
        grouped[key] = this.sortRemindersForDate(grouped[key]);
      });

      return grouped;
    },
    calendarCells() {
      const start = this.currentMonth.clone().startOf("month").startOf("week");
      const end = this.currentMonth.clone().endOf("month").endOf("week");
      const cells = [];
      const todayKey = moment().format("YYYY-MM-DD");
      const cursor = start.clone();

      while (cursor.isSameOrBefore(end, "day")) {
        const dateKey = cursor.format("YYYY-MM-DD");
        const dayReminders = this.remindersByDate[dateKey] || [];
        cells.push({
          date: dateKey,
          day: cursor.date(),
          inMonth: cursor.month() === this.currentMonth.month(),
          isToday: dateKey === todayKey,
          reminders: dayReminders,
          visibleReminders: dayReminders.slice(0, MAX_VISIBLE_REMINDERS),
          extraCount: Math.max(dayReminders.length - MAX_VISIBLE_REMINDERS, 0),
        });
        cursor.add(1, "day");
      }

      return cells;
    },
  },
  mounted() {
    this.loadReminders(true);
  },
  methods: {
    sortRemindersForDate(reminders) {
      return [...reminders].sort((a, b) => {
        const startDiff = moment(b.start_date).valueOf() - moment(a.start_date).valueOf();
        if (startDiff !== 0) {
          return startDiff;
        }
        return moment(b.created_at).valueOf() - moment(a.created_at).valueOf();
      });
    },
    openRemindersModal(cell) {
      if (!cell.reminders.length) {
        return;
      }
      this.selectedDateKey = cell.date;
      this.selectedDateReminders = [...cell.reminders].sort(
          (a, b) => moment(a.start_date).valueOf() - moment(b.start_date).valueOf()
      );
      this.remindersDialog = true;
    },
    reminderTitle(reminder) {
      const contactName = reminder.contact && reminder.contact.full_name
          ? reminder.contact.full_name
          : "Contact";
      return contactName;
    },
    formatReminderTime(date) {
      return moment(date).format("h:mm a");
    },
    syncMonthWithFilters() {
      if (this.filterStartDate) {
        this.currentMonth = moment(this.filterStartDate).startOf("month");
      }
    },
    loadReminders(logActivity = false) {
      this.loading = true;
      const payload = {
        module: this.selectedModule,
        start_date: this.filterStartDate,
        end_date: this.filterEndDate,
      };
      if (logActivity) {
        payload.reminders_calendar_activity_log = 1;
      }
      this.$store.dispatch(CALENDAR_REMINDERS, payload).then(data => {
        this.loading = false;
        if (data && data.records && data.records.reminders) {
          this.reminders = data.records.reminders;
        } else {
          this.reminders = [];
        }
        this.syncMonthWithFilters();
      });
    },
    previousMonth() {
      this.currentMonth = this.currentMonth.clone().subtract(1, "month");
      this.updateFilterRangeForMonth();
      this.loadReminders();
    },
    nextMonth() {
      this.currentMonth = this.currentMonth.clone().add(1, "month");
      this.updateFilterRangeForMonth();
      this.loadReminders();
    },
    goToToday() {
      this.currentMonth = moment().startOf("month");
      this.updateFilterRangeForMonth();
      this.loadReminders();
    },
    updateFilterRangeForMonth() {
      this.filterStartDate = this.currentMonth.clone().startOf("month").format("YYYY-MM-DD");
      this.filterEndDate = this.currentMonth.clone().endOf("month").format("YYYY-MM-DD");
    },
  },
};
</script>

<style scoped>
.calendar-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  border: 1px solid #e0e0e0;
}

.calendar-weekday {
  padding: 8px 4px;
  background: #f5f5f5;
  border-bottom: 1px solid #e0e0e0;
  font-size: 13px;
}

.calendar-cell {
  min-height: 110px;
  border-right: 1px solid #e0e0e0;
  border-bottom: 1px solid #e0e0e0;
  padding: 4px;
  background: #fff;
}

.calendar-cell:nth-child(7n) {
  border-right: none;
}

.calendar-cell--muted {
  background: #fafafa;
  color: #9e9e9e;
}

.calendar-cell--today {
  background: #f3f6ff;
}

.calendar-cell__day {
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 4px;
}

.calendar-cell__events {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.calendar-event {
  background: #00004d;
  color: #fff;
  border-radius: 3px;
  padding: 2px 4px;
  font-size: 11px;
  line-height: 1.3;
  cursor: pointer;
  overflow: hidden;
}

.calendar-event--more {
  background: #5c5c8a;
  font-weight: 600;
  text-align: center;
}

.calendar-event__time {
  font-weight: 600;
  margin-right: 4px;
}

.calendar-event__title {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
